import {
  FeedDataState,
  PostUid,
  FollowedPostsCount,
  Post,
  FeedUser,
  Comment,
  FilterUpdates,
  FollowedUsersLoading,
  FollowedTagsLoading,
  FilterPostsLoading,
  RandomFeedRelationState,
  PostsCollection,
  PostByFilterState,
  FeedUserUid,
  UserSuggestsDataState,
  TagsSuggestsDataState,
  TagKeysBySearch,
  TagCollection,
  FeedTag,
  TagKeysWithRequestDetails,
  POST_TYPE_SOCIAL_INSIGHT,
  FeedUsersState,
} from 'interfaces/feed'
import { Reducer, Omit } from 'interfaces'
import { combineReducers } from 'redux'
import uniq from 'lodash/uniq'
import pick from 'lodash/pick'
import {
  GET_POSTS,
  LOAD_FOLLOWED_POSTS_COUNT,
  TOGGLE_POST_LIKE,
  TOGGLE_POST_FOLLOWING,
  TOGGLE_AUTHOR_FOLLOWING,
  TOGGLE_COMMENT_LIKE,
  REPORT_COMMENT,
  REPORT_POST,
  GET_FILTERS_STATUS,
  GET_COMMENTS,
  GET_FOLLOWED_USERS,
  GET_FOLLOWED_TAGS,
  GET_FEED_AUTHOR,
  GET_FEED_TAG,
  TOGGLE_TAG_FOLLOWING,
  GET_RANDOM_FEED_RELATION,
  RANDOMIZE_FEED_RELATION,
  DELETE_COMMENT,
  DELETE_POST,
  RESET_FOLLOWED_USERS,
  RESET_FOLLOWED_TAGS,
  GET_FEED_MEMBER_INFO,
  LOAD_COMMENT_THREAD,
  REPORT_INSIGHT,
  GET_FEED_TAGS,
} from 'actions/feedActions'
import { isSuccessAction, isRequestAction, isFailAction } from 'helpers/actionsHelpers'
import { createStaleableData } from 'helpers/storeHelpers'
import { POSTS_REFRESH_TRIGGER, needDiscardData } from 'helpers/discardTriggerHelpers'
import {
  concatCollections,
  createEmptyCollection,
  setItemInCollection,
  getItemFromCollection,
  concatPaginatables,
  removeByKeyFromCollection,
  filterCollection,
  getItemsByKey,
} from 'helpers/normalizeHelpers'
import { serializeFeedFilterQuery, getNextCounterValue, createEmptyThread, isSocialInsightPost } from 'helpers/feedHelpers'
import { createLoadingSingleReducer, createToggleMarkReducer } from 'helpers/reducerHelpers'
import { FORM_SUBMITTED_SUCCESSFULLY } from 'actions/formActions'
import { FORM_MEMBER_PROFILE, FORM_SOCIAL_PROFILE } from 'constants/forms'
import { COMPOSE_SUBMIT, SUGGEST_USERS, SUGGEST_TAGS } from 'actions/feedComposeActions'
import { encodeUrlParams } from 'helpers/utilsHelpers'
import { GET_SOCIAL_INSIGHTS, TOGGLE_SOCIAL_INSIGHT_LIKE } from 'actions/socialInsightsActions'

const convertMemberToUser = (member: Partial<FeedUser>, uid: FeedUserUid): FeedUser => {
  return {
    uid,
    is_followed: false,
    badges: [],
    username: '',
    ...member,
    avatarId: member.avatarId || null,
    followers_count: 0,
    likes_count: 0,
    posts_count: 0,
  }
}

type FeedEntityLikeFieldsMapping = {
  id: 'uid'
  isMarked: 'is_liked',
  marksCount: 'likes_count'
}

type FeedEntityFollowFieldsMapping<TId extends string = 'uid'> = {
  id: TId
  isMarked: 'is_followed',
  marksCount: 'followers_count'
}

const toggleUserFollowingReducer = createToggleMarkReducer<Omit<FeedEntityFollowFieldsMapping, 'marksCount'>, FeedUser>({
  getActionPayload: (action) => {
    if (action.type === TOGGLE_AUTHOR_FOLLOWING) {
      const { uid, toFollow } = action.payload.params

      return {
        id: uid,
        toMark: toFollow,
        count: null,
      }
    }

    return null
  },
  fieldsMapping: {
    id: 'uid',
    isMarked: 'is_followed',
  },
})


const usersReducer: Reducer<FeedDataState['users']> = (prevState = createEmptyCollection(), action) => {
  switch (action.type) {
    case FORM_SUBMITTED_SUCCESSFULLY: {
      if (action.payload.formName === FORM_MEMBER_PROFILE) {
        const { response, extra } = action.payload
        const nextUser: FeedUser = convertMemberToUser(response.feedMember, extra.uid)

        return setItemInCollection(prevState, nextUser, 'uid', { mergeIfExist: true })
      }

      if (action.payload.formName === FORM_SOCIAL_PROFILE) {
        const { response, extra } = action.payload
        const nextUser: FeedUser = {
          ...convertMemberToUser(response, extra.uid),
          ...response.avatar ? { avatarId: response.avatar.id } : {},
        }

        return setItemInCollection(prevState, nextUser, 'uid', { mergeIfExist: true })
      }

      return prevState
    }
    case GET_FEED_AUTHOR: {
      if (isSuccessAction(action)) {
        return setItemInCollection(prevState, action.payload.response, 'uid')
      }

      break
    }

    case GET_FEED_MEMBER_INFO: {
      if (isSuccessAction(action)) {
        return setItemInCollection(prevState, action.payload.response.member, 'uid')
      }

      break
    }

    case GET_POSTS:
    case LOAD_COMMENT_THREAD:
    case GET_COMMENTS: {
      if (
        action.type === GET_POSTS && isSuccessAction(action) ||
        action.type === LOAD_COMMENT_THREAD && isSuccessAction(action) ||
        action.type === GET_COMMENTS && isSuccessAction(action)
      ) {
        const { users } = action.payload.response

        // author whom we receive with posts never contains `peergroup` prop
        // but author whom we receive from `/socialfeed/users/?username=` have `peergroup` because we have to render user avatar for whole authors page
        // to avoid rewriting of `peergroup` prop we have to carry it through concat process
        return getItemsByKey(prevState.byKey, users.allKeys)
          .reduce((nextState, prevUser) => {
            const nextUser = users.byKey[prevUser.uid]
            if (prevUser.peergroup && !nextUser.peergroup) {
              nextState = setItemInCollection(nextState, {
                ...nextUser,
                peergroup: prevUser.peergroup,
              }, 'uid')
            }

            return nextState
          }, concatCollections<FeedUser, 'uid'>(prevState, users))
      }

      break
    }

    case GET_FOLLOWED_USERS: {
      if (isSuccessAction(action)) {
        return concatCollections(prevState, action.payload.response.users)
      }

      break
    }

    case TOGGLE_AUTHOR_FOLLOWING: {
      return toggleUserFollowingReducer(action, prevState) as FeedUsersState
    }

    case TOGGLE_POST_LIKE:
    case TOGGLE_COMMENT_LIKE: {
      const isRequest = action.type === TOGGLE_POST_LIKE && isRequestAction(action)
        || action.type === TOGGLE_COMMENT_LIKE && isRequestAction(action)
      const isFail = action.type === TOGGLE_POST_LIKE && isFailAction(action)
        || action.type === TOGGLE_COMMENT_LIKE && isFailAction(action)

      if (isRequest || isFail) {
        const { authorUid, toLike } = action.payload.params
        const prevItem = getItemFromCollection<FeedUser, 'uid'>(prevState, authorUid)
        if (!prevItem) {
          return prevState
        }

        const nextItem: FeedUser = {
          ...prevItem,
          likes_count: getNextCounterValue(prevItem.likes_count, toLike, isRequest),
        }

        return setItemInCollection(prevState, nextItem, 'uid')
      }
    }
  }

  return prevState
}

const toggleCommentLikeReducer = createToggleMarkReducer<FeedEntityLikeFieldsMapping, Comment>({
  getActionPayload: (action) => {
    if (action.type === TOGGLE_COMMENT_LIKE) {
      const { commentUid, toLike } = action.payload.params

      return {
        id: commentUid,
        toMark: toLike,
        count: isSuccessAction(action) ? action.payload.response.count : null,
      }
    }

    return null
  },
  fieldsMapping: {
    id: 'uid',
    isMarked: 'is_liked',
    marksCount: 'likes_count',
  },
})

const comments: Reducer<FeedDataState['comments']> = (prevState = createEmptyCollection(), action) => {
  switch (action.type) {

    case GET_POSTS: {
      if (isSuccessAction(action)) {
        return concatCollections(prevState, action.payload.response.comments)
      }

      break
    }

    case DELETE_COMMENT: {
      if (isSuccessAction(action)) {
        const removedComment = getItemFromCollection(prevState, action.payload.params.uid)
        if (!removedComment) {
          return prevState
        }

        let nextState = removeByKeyFromCollection(prevState, action.payload.params.uid)
        prevState.allKeys.forEach((uid) => {
          const comment = prevState.byKey[uid]

          if (comment.postUid !== removedComment.postUid || !comment.children || !comment.children.results) {
            return
          }

          const prevResults = comment.children.results
          const nextResults = prevResults.filter((commentUid) => commentUid !== removedComment.uid)
          const commentsCountDelta = prevResults.length - nextResults.length
          if (commentsCountDelta === 0) {
            return
          }

          const nextChildrenCount = comment.childrenCount - commentsCountDelta
          const nextChildren = {
            ...comment.children,
            results: nextResults,
          }
          nextState = setItemInCollection(nextState, {
            ...comment,
            children: nextChildren,
            childrenCount: nextChildrenCount,
          }, 'uid')
        })
        return nextState
      }

      break
    }

    case REPORT_INSIGHT:
    case DELETE_POST: {
      if (!isSuccessAction(action)) {
        break
      }

      const { postUid } = action.payload.params

      return filterCollection(
        (comment) => comment.postUid !== postUid,
        prevState,
      )
    }

    case TOGGLE_COMMENT_LIKE: {
      return toggleCommentLikeReducer(action, prevState)
    }

    case REPORT_COMMENT: {
      if (isSuccessAction(action)) {
        const { uid: commentUid } = action.payload.params

        const prevItem = getItemFromCollection(prevState, commentUid)
        if (!prevItem) {
          return prevState
        }

        const nextItem: Comment = {
          ...prevItem,
          is_reported: true,
        }

        return setItemInCollection(prevState, nextItem, 'uid')
      }

      break
    }

    case LOAD_COMMENT_THREAD: {
      const { commentUid } = action.payload.params
      const prevComment = getItemFromCollection(prevState, commentUid)
      if (!prevComment || !prevComment.children) {
        return prevState
      }

      if (isRequestAction(action)) {
        const nextChildren = {
          ...prevComment.children,
          isLoading: true,
          errorCode: null,
        }

        return setItemInCollection(prevState, {
          ...prevComment,
          children: nextChildren,
        }, 'uid')
      }

      if (isFailAction(action)) {
        const { errorCode } = action.payload
        const nextChildren = {
          ...prevComment.children,
          isLoading: false,
          errorCode,
        }

        return setItemInCollection(prevState, {
          ...prevComment,
          children: nextChildren,
        }, 'uid')
      }

      {
        const { thread, comments: nextComments } = action.payload.response

        const nextResults = uniq([
          ...prevComment.children.results,
          ...thread.results,
        ])

        const nextChildren = {
          ...prevComment.children,
          isLoading: false,
          errorCode: null,
          results: nextResults,
          pagination: thread.pagination,
        }

        const nextState = setItemInCollection(prevState, {
          ...prevComment,
          children: nextChildren,
        }, 'uid')

        return concatCollections(nextState, nextComments)
      }
    }

    case GET_COMMENTS: {
      if (isSuccessAction(action)) {
        return concatCollections(prevState, action.payload.response.comments)
      }

      break
    }

    case COMPOSE_SUBMIT: {
      if (!isSuccessAction(action)) {
        break
      }

      const { response } = action.payload
      if (response.type !== 'comment') {
        break
      }

      const prevComment = getItemFromCollection(prevState, response.comment.uid)
      let nextComment = response.comment
      if (prevComment) {
        nextComment = {
          ...prevComment,
          ...pick(response.comment, ['mentions', 'imagesIds', 'links', 'text']),
        }
      }

      let nextState = setItemInCollection(prevState, nextComment, 'uid')

      if (response.parentCommentUid) {
        const parentComment = getItemFromCollection(nextState, response.parentCommentUid)

        if (parentComment) {
          const nextResults = [
            ...parentComment.children ? parentComment.children.results : [],
            response.comment.uid,
          ]

          const nextChildren = {
            isLoading: false,
            errorCode: null,
            pagination: { next: null, previous: null },
            ...parentComment.children,
            results: nextResults,
          }

          nextState = setItemInCollection(nextState, {
            ...parentComment,
            children: nextChildren,
            childrenCount: parentComment.childrenCount + 1,
          }, 'uid')
        }
      }

      return nextState
    }

    default:
      return prevState
  }

  return prevState
}

const togglePostLikeReducer = createToggleMarkReducer<FeedEntityLikeFieldsMapping, Post>({
  getActionPayload: (action) => {
    if (action.type === TOGGLE_POST_LIKE) {
      const { postUid, toLike } = action.payload.params

      return {
        id: postUid,
        toMark: toLike,
        count: isSuccessAction(action) ? action.payload.response.count : null,
      }
    }

    if (action.type === TOGGLE_SOCIAL_INSIGHT_LIKE) {
      const { socialInsightUid, toLike } = action.payload.params

      return {
        id: socialInsightUid,
        toMark: toLike,
        count: isSuccessAction(action) ? action.payload.response.count : null,
      }
    }

    return null
  },
  fieldsMapping: {
    id: 'uid',
    isMarked: 'is_liked',
    marksCount: 'likes_count',
  },
})

const togglePostFollowReducer = createToggleMarkReducer<FeedEntityFollowFieldsMapping, Exclude<Post, { type: typeof POST_TYPE_SOCIAL_INSIGHT }>>({
  getActionPayload: (action) => {
    if (action.type === TOGGLE_POST_FOLLOWING) {
      const { postUid, toFollow } = action.payload.params

      return {
        id: postUid,
        toMark: toFollow,
        count: isSuccessAction(action) ? action.payload.response.count : null,
      }
    }

    return null
  },
  fieldsMapping: {
    id: 'uid',
    isMarked: 'is_followed',
    marksCount: 'followers_count',
  },
})

const allPosts: Reducer<PostsCollection> = (prevState = createEmptyCollection(), action): PostsCollection => {
  switch (action.type) {

    case GET_POSTS: {
      if (isSuccessAction(action)) {
        return concatCollections(prevState, action.payload.response.posts)
      }

      break
    }

    case GET_SOCIAL_INSIGHTS: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const { socialInsights } = action.payload.response

      return concatCollections<Post, 'uid'>(prevState, socialInsights)
    }


    case TOGGLE_SOCIAL_INSIGHT_LIKE:
    case TOGGLE_POST_LIKE: {
      return togglePostLikeReducer(action, prevState) as PostsCollection
    }

    case TOGGLE_POST_FOLLOWING: {
      const { postUid } = action.payload.params
      const prevItem = getItemFromCollection<Post, 'uid'>(prevState, postUid)
      if (!prevItem || isSocialInsightPost(prevItem)) {
        return prevState
      }

      const nextItem = togglePostFollowReducer.toggleItemMark(action, prevItem)
      if (!nextItem || nextItem === prevItem) {
        return prevState
      }

      return setItemInCollection<Post, 'uid'>(prevState, nextItem, 'uid')
    }

    case REPORT_POST: {
      if (isSuccessAction(action)) {
        const { postUid } = action.payload.params

        const prevItem = getItemFromCollection<Post, 'uid'>(prevState, postUid)
        if (!prevItem || prevItem.type === POST_TYPE_SOCIAL_INSIGHT) {
          return prevState
        }

        const nextItem: Post = {
          ...prevItem,
          is_reported: true,
        }

        return setItemInCollection<Post, 'uid'>(prevState, nextItem, 'uid')
      }

      break
    }

    case GET_COMMENTS: {
      if (isSuccessAction(action)) {
        const { postUid, ordering } = action.payload.params
        const { thread } = action.payload.response
        const prevPost = getItemFromCollection<Post, 'uid'>(prevState, postUid)
        // there is the only case when we need to discard comments: when `ordering` is changed explicitly
        const toDiscardItems = Boolean(ordering)

        if (!prevPost) {
          return prevState
        }

        const { comments: prevComments } = prevPost

        let nextComments = thread
        if (!toDiscardItems) {
          const concatedComments = prevComments ? concatPaginatables(prevComments, thread) : thread
          concatedComments.results = uniq(concatedComments.results)

          nextComments = {
            ...createEmptyThread(),
            ...prevComments,
            ...concatedComments,
          }
        }


        const nextItem: Post = {
          ...prevPost,
          comments: nextComments,
        }

        return setItemInCollection(prevState, nextItem, 'uid')
      }

      if (isRequestAction(action) && action.payload.params.ordering) {
        const prevItem = getItemFromCollection<Post, 'uid'>(prevState, action.payload.params.postUid)
        if (prevItem && prevItem.commentsOrdering !== action.payload.params.ordering) {
          return setItemInCollection<Post, 'uid'>(
            prevState,
            {
              uid: action.payload.params.postUid,
              commentsOrdering: action.payload.params.ordering,
            },
            'uid',
            { mergeIfExist: true }
          )
        }

        return prevState
      }

      break
    }

    case DELETE_COMMENT: {
      if (isSuccessAction(action)) {
        const { uid: commentUid, postUid } = action.payload.params
        const prevPost = getItemFromCollection<Post, 'uid'>(prevState, postUid)
        if (!prevPost) {
          return prevState
        }

        const { childrenCount } = action.payload.response
        const nextPost: Post = {
          ...prevPost,
          comments: {
            ...createEmptyThread(),
            ...prevPost.comments,
            results: prevPost.comments ? prevPost.comments.results.filter((id) => id !== commentUid) : [],
          },
          comments_count: prevPost.comments_count - 1 - childrenCount,
        }

        return setItemInCollection(prevState, nextPost, 'uid')
      }

      break
    }

    case REPORT_INSIGHT:
    case DELETE_POST: {
      if (!isSuccessAction(action)) {
        break
      }

      const { postUid } = action.payload.params
      return removeByKeyFromCollection<Post, 'uid'>(prevState, postUid)
    }

    case COMPOSE_SUBMIT: {
      if (!isSuccessAction(action)) {
        break
      }

      const { response } = action.payload

      if (response.type === 'comment') {
        const { comment } = response
        const prevPost = getItemFromCollection<Post, 'uid'>(prevState, comment.postUid)

        if (!prevPost || response.isEdit) {
          return prevState
        }

        const comments_count = prevPost.comments_count + 1

        if (response.parentCommentUid) {
          return setItemInCollection(prevState, {
            ...prevPost,
            comments_count,
          }, 'uid')
        }

        const results = uniq([
          ...prevPost.comments ? prevPost.comments.results : [],
          comment.uid,
        ])
        const followersObj = 'followers_count' in prevPost
          ? {
            followerd_count: prevPost.followers_count + (prevPost.is_followed ? 0 : 1),
            is_followed: true,
          }
          : null

        const nextState: PostsCollection = setItemInCollection<Post, 'uid'>(prevState, {
          ...prevPost,
          comments: {
            ...createEmptyThread(),
            ...prevPost.comments,
            results,
          },
          comments_count,
          ...followersObj,
        }, 'uid')
        return nextState

      } else if (response.type === 'post') {
        const nextState: PostsCollection = setItemInCollection(prevState, response.post, 'uid')
        return nextState
      }
    }
  }

  return prevState
}

const createDefaultFilterPostsLoadingState = (): FilterPostsLoading => ({
  pagination: null,
  isLoading: false,
  errorCode: null,
})

const postsByFilter: Reducer<PostByFilterState> = (prevState = {}, action) => {
  if (needDiscardData(POSTS_REFRESH_TRIGGER)(action)) {
    return {}
  }

  if (action.type === DELETE_POST || action.type === REPORT_INSIGHT) {
    if (!isSuccessAction(action)) {
      return prevState
    }

    const { postUid } = action.payload.params

    return Object.keys(prevState).reduce<FeedDataState['postsByFilter']>((acc, key) => {
      const prevFilterPosts = prevState[key]
      if (
        !prevFilterPosts
        || prevFilterPosts.isStale
        || !prevFilterPosts.data.pagination
      ) {
        acc[key] = prevFilterPosts
        return acc
      }

      acc[key] = {
        ...prevFilterPosts,
        data: {
          ...prevFilterPosts.data,
          pagination: {
            ...prevFilterPosts.data.pagination,
            results: prevFilterPosts.data.pagination.results.filter((uid) => uid !== postUid),
          },
        },
      }

      return acc
    }, {})
  }

  if (action.type === COMPOSE_SUBMIT) {
    if (!isSuccessAction(action)) {
      return prevState
    }

    const { response } = action.payload
    if (response.type !== 'post') {
      return prevState
    }

    const { post, feedFilter } = response
    if (!feedFilter) {
      return prevState
    }

    const stringifiedQuery = serializeFeedFilterQuery(feedFilter)
    const prevPostsState = prevState[stringifiedQuery]
    if (!prevPostsState || !prevPostsState.data.pagination) {
      return prevState
    }

    const nextState: PostByFilterState = {
      ...prevState,
      [stringifiedQuery]: {
        ...prevPostsState,
        data: {
          ...prevPostsState.data,
          pagination: {
            ...prevPostsState.data.pagination,
            results: uniq([
              post.uid,
              ...prevPostsState.data.pagination.results,
            ]),
          },
        },
      },
    }

    return nextState
  }

  if (action.type === GET_POSTS) {
    const stringifiedQuery = serializeFeedFilterQuery(action.payload.params.query)
    const prevPostsState = prevState[stringifiedQuery]
    const prevPostsDate = prevPostsState ? prevPostsState.data : createDefaultFilterPostsLoadingState()

    if (isRequestAction(action)) {
      return {
        ...prevState,
        [stringifiedQuery]: createStaleableData<FilterPostsLoading>({
          ...prevPostsDate,
          isLoading: true,
        }),
      }
    }

    if (isFailAction(action)) {
      return {
        ...prevState,
        [stringifiedQuery]: createStaleableData<FilterPostsLoading>({
          ...prevPostsDate,
          isLoading: false,
          errorCode: action.payload.errorCode,
        }),
      }
    }

    if (isSuccessAction(action)) {
      const prevPostUids: PostUid[] = prevPostsState && !prevPostsState.isStale && prevPostsState.data.pagination
        ? prevPostsState.data.pagination.results
        : []
      const nextPostUids: PostUid[] = uniq(
        prevPostUids.concat(action.payload.response.postsByFilter.results)
      )

      const nextState: PostByFilterState = {
        ...prevState,
        [stringifiedQuery]: createStaleableData<FilterPostsLoading>({
          pagination: {
            pagination: action.payload.response.postsByFilter.pagination,
            results: nextPostUids,
          },
          isLoading: false,
          errorCode: null,
        }),
      }
      return nextState
    }
  }

  return prevState
}


const followedPostsCount: Reducer<FollowedPostsCount | null> = (prevState = null, action) => {
  switch (action.type) {
    case LOAD_FOLLOWED_POSTS_COUNT:
      return isSuccessAction(action) ? action.payload.response : prevState

    case TOGGLE_POST_FOLLOWING:
      // null forces popup to reload to consider case when user follows/unfollows on another device
      return isSuccessAction(action) ? null : prevState

    case COMPOSE_SUBMIT: {
      return isSuccessAction(action) && action.payload.response.type === 'comment'
        ? null
        : prevState
    }

    default:
      return prevState
  }
}

const filterUpdates: Reducer<FilterUpdates> = (prevState = {}, action) => {
  switch (action.type) {
    case GET_POSTS: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const stringifiedQuery = serializeFeedFilterQuery(action.payload.params.query)
      return {
        ...prevState,
        [stringifiedQuery]: false,
      }
    }

    case GET_FILTERS_STATUS: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      const newLoadedFlags = action.payload.params
        .map((params, idx) => ({
          query: serializeFeedFilterQuery(params),
          isUpdated: action.payload.response[idx] ? action.payload.response[idx].is_updated : false,
        }))
        .reduce<FilterUpdates>((acc, item) => {
          acc[item.query] = item.isUpdated
          return acc
        }, {})

      return {
        ...prevState,
        ...newLoadedFlags,
      }
    }

    default:
      return prevState
  }
}

const INITIAL_FOLLOWED_USERS_LOADING: FollowedUsersLoading = {
  pagination: {
    results: [],
    pagination: null,
  },
  isLoading: false,
  errorCode: null,
}
const followedUsersLoading: Reducer<FollowedUsersLoading> = (prevState = INITIAL_FOLLOWED_USERS_LOADING, action) => {
  switch (action.type) {
    case GET_FOLLOWED_USERS: {
      if (isSuccessAction(action)) {
        const { users, links } = action.payload.response

        return prevState.isLoading
          ? {
            pagination: {
              results: prevState.pagination.results.concat(users.allKeys),
              pagination: links,
            },
            isLoading: false,
            errorCode: null,
          }
          : prevState
      }

      if (isRequestAction(action)) {
        return {
          ...prevState,
          isLoading: true,
        }
      }

      return prevState.isLoading
        ? {
          ...prevState,
          isLoading: false,
          errorCode: action.payload.errorCode,
        }
        : prevState
    }

    case RESET_FOLLOWED_USERS:
      return INITIAL_FOLLOWED_USERS_LOADING

    default:
      return prevState
  }
}

const toggleTagFollowingReducer = createToggleMarkReducer<Omit<FeedEntityFollowFieldsMapping<'name'>, 'marksCount'>, FeedTag>({
  getActionPayload: (action) => {
    if (action.type === TOGGLE_TAG_FOLLOWING) {
      const { tag_name, toFollow } = action.payload.params

      return {
        id: tag_name,
        toMark: toFollow,
        count: null,
      }
    }

    return null
  },
  fieldsMapping: {
    id: 'name',
    isMarked: 'is_followed',
  },
})

const allTags: Reducer<TagCollection> = (prevState = createEmptyCollection(), action) => {
  switch (action.type) {
    case GET_FOLLOWED_TAGS: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      return concatCollections<FeedTag, 'name'>(prevState, action.payload.response.tags)
    }

    case GET_FEED_TAG: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      return setItemInCollection(prevState, action.payload.response, 'name', { mergeIfExist: true })
    }

    case TOGGLE_TAG_FOLLOWING: {
      return toggleTagFollowingReducer(action, prevState) as TagCollection
    }

    case GET_FEED_TAGS: {
      if (!isSuccessAction(action)) {
        return prevState
      }

      return concatCollections<FeedTag, 'name'>(prevState, action.payload.response.payload)
    }
  }

  return prevState
}

const INITIAL_FOLLOWED_TAGS_LOADING: FollowedTagsLoading = {
  pagination: {
    results: [],
    pagination: null,
  },
  isLoading: false,
  errorCode: null,
}
const followedTagsLoading: Reducer<FollowedTagsLoading> = (prevState = INITIAL_FOLLOWED_TAGS_LOADING, action) => {
  switch (action.type) {
    case GET_FOLLOWED_TAGS: {
      if (isSuccessAction(action)) {
        const { tags, links } = action.payload.response

        return prevState.isLoading
          ? {
            pagination: {
              results: prevState.pagination.results.concat(tags.allKeys),
              pagination: links,
            },
            isLoading: false,
            errorCode: null,
          }
          : prevState
      }

      if (isRequestAction(action)) {
        return {
          ...prevState,
          isLoading: true,
        }
      }

      return prevState.isLoading
        ? {
          ...prevState,
          isLoading: false,
          errorCode: action.payload.errorCode,
        }
        : prevState
    }

    case RESET_FOLLOWED_TAGS:
      return INITIAL_FOLLOWED_TAGS_LOADING

    default:
      return prevState
  }
}


const isRandomFeedRelationLoading = createLoadingSingleReducer(
  GET_RANDOM_FEED_RELATION,
)

const isRandomFeedRelationRandomizing = createLoadingSingleReducer(
  RANDOMIZE_FEED_RELATION,
)

const randomRelationReducer: Reducer<RandomFeedRelationState['data']> = (prevState = null, action) => {
  switch (action.type) {
    case GET_RANDOM_FEED_RELATION:
    case RANDOMIZE_FEED_RELATION: {
      return isSuccessAction(action)
        ? action.payload.response
        : prevState
    }

    default:
      return prevState
  }
}

const SUGGESTS_INITIAL = {
  isLoading: false,
  errorCode: null,
  payload: [],
}
const suggestedUsers: Reducer<UserSuggestsDataState> = (prevState = SUGGESTS_INITIAL, action): UserSuggestsDataState => {
  switch (action.type) {
    case SUGGEST_USERS: {
      if (isRequestAction(action)) {
        return {
          ...prevState,
          isLoading: true,
          errorCode: null,
        }
      }
      if (isFailAction(action)) {
        return {
          ...prevState,
          isLoading: false,
          errorCode: action.payload.errorCode,
        }
      }

      const nextSuggests = action.payload.response
      return {
        ...prevState,
        errorCode: null,
        isLoading: false,
        payload: nextSuggests,
      }
    }
  }
  return prevState
}

const TAGS_SUGGESTS_INITIAL = {
  isLoading: false,
  errorCode: null,
  payload: [],
}
const suggestedTags: Reducer<TagsSuggestsDataState> = (prevState = TAGS_SUGGESTS_INITIAL, action): TagsSuggestsDataState => {
  switch (action.type) {
    case SUGGEST_TAGS: {
      if (isRequestAction(action)) {
        return {
          ...prevState,
          isLoading: true,
          errorCode: null,
        }
      }
      if (isFailAction(action)) {
        return {
          ...prevState,
          isLoading: false,
          errorCode: action.payload.errorCode,
        }
      }

      const nextSuggests = action.payload.response
      return {
        ...prevState,
        errorCode: null,
        isLoading: false,
        payload: nextSuggests,
      }
    }
  }
  return prevState
}


const TAGS_BY_SEARCH_INITIAL_STATE: TagKeysBySearch = {}
const tagsBySearch: Reducer<TagKeysBySearch> = (prevState = TAGS_BY_SEARCH_INITIAL_STATE, action) => {
  switch (action.type) {
    case GET_FEED_TAGS: {
      const key = encodeUrlParams(action.payload.params)
      const prevValue = key in prevState ? prevState[key] : {}

      let nextValue: TagKeysWithRequestDetails | undefined = undefined
      if (isRequestAction(action)) {
        nextValue = {
          ...prevValue,
          search: key,
          isLoading: true,
          errorCode: null,
          payload: null,
        }
      }
      if (isFailAction(action)) {
        nextValue = {
          ...prevValue,
          search: key,
          isLoading: false,
          errorCode: action.payload.errorCode,
          payload: null,
        }
      }

      if (isSuccessAction(action)) {
        nextValue = {
          search: key,
          isLoading: false,
          errorCode: null,
          payload: action.payload.response.payload.allKeys,
        }
      }

      if (!nextValue) {
        return prevState
      }

      return {
        ...prevState,
        [key]: nextValue,
      }
    }

    default:
      return prevState
  }
}

export default combineReducers<FeedDataState>({
  allTags,
  tagsBySearch,
  users: usersReducer,
  comments,
  allPosts,
  postsByFilter,
  followedPostsCount,
  filterUpdates,
  followedUsersLoading,
  followedTagsLoading,
  randomFeedRelation: combineReducers<RandomFeedRelationState>({
    isLoading: isRandomFeedRelationLoading,
    isRandomizing: isRandomFeedRelationRandomizing,
    data: randomRelationReducer,
  }),
  suggestedUsers,
  suggestedTags,
})
